// src/pages/ListCards.jsx
/**
 * pages/ListCards.jsx
 * --------------------
 * Main card inventory view — the primary card management interface.
 *
 * Key features:
 *   - Paginated table of all user cards with sortable columns
 *   - Multi-column sort via SortModal (up to N levels, can be saved as default)
 *   - Inline editing: click row or edit button → edit form in row → save PUT /cards/:id
 *   - Row color coding: rookie (gold), grade 3.0 (lavender), rookie+grade3 (blue)
 *     Colors sourced from GlobalSettings
 *   - Pinned row (bookmark): persisted to GlobalSettings (DB) — survives logout/device switch
 *   - CD-player pagination controls in thead action column
 *   - Image viewer modal (CardImages) for front/back card photos
 *   - Label print via LabelPreviewModal → /card-label/:id
 *   - Batch label print: select multiple rows → /batch-labels with cardIds state
 *   - Quick navigation to CardDetail passing full sorted cardIds list for prev/next nav
 *
 * Sticky header: card-section uses overflow:auto + maxHeight calc(100vh-180px)
 * so thead th { position: sticky; top: 0 } works within the scroll container.
 */
import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../api/api";
import AppHeader from "../components/AppHeader";
import CardImages from "../components/CardImages";
import ImageEditor from "../components/ImageEditor";
import LabelPreviewModal from "../components/LabelPreviewModal";
import SortModal, { SORT_COLUMNS } from "../components/SortModal";

const BOOK_FIELDS = ["book_high", "book_high_mid", "book_mid", "book_low_mid", "book_low"];

const fmtDollar = (n) => {
  const val = Math.round(Number(n || 0));
  return `$${val.toLocaleString()}`;
};


export default function ListCards() {  // NOSONAR: React component complexity is inherent
  const { user } = useAuth();
  const isAdmin = user?.is_admin ?? false;
  const tierAllowsLabels = ({ base: 0, rc: 1, sp: 2, elite: 3 }[user?.subscription_tier ?? "base"] ?? 0) >= 3;
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = location.state || {};

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [settings, setSettings] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [hoverCard, setHoverCard] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const [lastNameFilter, setLastNameFilter] = useState(returnState.lastNameFilter ?? "");
  const [brandFilter, setBrandFilter] = useState(returnState.brandFilter ?? "");
  const [gradeFilter, setGradeFilter] = useState(returnState.gradeFilter ?? "");
  const [yearFilter, setYearFilter] = useState(returnState.yearFilter ?? "");

  // Debounced versions — only update after 300ms pause, preventing mid-typing fetches
  const [dLastName, setDLastName] = useState(returnState.lastNameFilter ?? "");
  const [dBrand, setDBrand] = useState(returnState.brandFilter ?? "");
  const [dGrade, setDGrade] = useState(returnState.gradeFilter ?? "");
  const [dYear, setDYear] = useState(returnState.yearFilter ?? "");
  const [sortConfig, setSortConfig] = React.useState(Array.isArray(returnState.sortConfig) ? returnState.sortConfig : []);
  const [hideDuplicates, setHideDuplicates] = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);
  const [returnCardId, setReturnCardId] = useState(returnState.returnCardId ?? null);
  const [highlightCardId, setHighlightCardId] = useState(returnState.highlightCardId ?? null);
  const [pinnedCard, setPinnedCard] = useState(null);
  const [editingCardId, setEditingCardId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [cloningCardId, setCloningCardId] = useState(null);
  const [cloningParentId, setCloningParentId] = useState(null);
  const [displaySnapshot, setDisplaySnapshot] = useState(null); // frozen ordered id list during clone session
  const [focusCardId, setFocusCardId] = useState(returnState.scrollToCardId ?? null);
  const mountScrollCardIdRef = useRef(returnState.scrollToCardId ?? null);
  const [pinnedRowId, setPinnedRowId] = useState(null);
  const rowRefsMap = useRef({});
  const [variantOpenId, setVariantOpenId] = useState(null);
  const [variantForm, setVariantForm] = useState({});
  const [labelData, setLabelData] = useState(null);
  const [labelLoading, setLabelLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [openFilterCols, setOpenFilterCols] = useState(new Set());
  const [playerNames, setPlayerNames] = useState({ firstNames: [], lastNames: [] });
  const skipNextFetchRef = React.useRef(false);
  const defaultSortApplied = useRef(false);
  const tableSectionRef = useRef(null);
  const [deleteConfirmCard, setDeleteConfirmCard] = useState(null);
  const deleteAnchorIdRef = useRef(null);
  const filterInputRefs = useRef({ last: null, year: null, brand: null, grade: null });
  const origBookVals = useRef({});
  const autoEditRef = useRef(false);
  const [toast, setToast] = useState(null);
  const [bookValueClipboard, setBookValueClipboard] = useState(null);
  const [bookEditMode, setBookEditMode] = useState(false);
  const [chipEdit, setChipEdit] = useState(null); // { id, field, value }
  const [gradedFilter, setGradedFilter] = useState("all"); // "all"|"graded"|"psa"|"sgc"|"bgc"|"raw"
  const [dGraded, setDGraded] = useState("all");
  const [refreshTick, setRefreshTick] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [currentSkip, setCurrentSkip] = useState(0);
  const [totalValue, setTotalValue] = useState(0);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  // null = load all; undefined/not set = default 250
  // Can't use ?? because null ?? 250 = 250 (nullish coalescing falls through null)
  const PAGE_SIZE = (settingsLoaded && settings && 'cards_per_page' in settings)
    ? settings.cards_per_page   // null = all, or explicit number
    : 250;
  const FETCH_LIMIT = PAGE_SIZE === null ? 999999 : PAGE_SIZE;
  const [jumpRate, setJumpRate] = useState(50);
  const [inlineEditorFile, setInlineEditorFile] = useState(null);
  const [inlineEditorSide, setInlineEditorSide] = useState(null);
  const [inlineEditorCardId, setInlineEditorCardId] = useState(null);
  const [inlineEditorIsExisting, setInlineEditorIsExisting] = useState(false);

  const enableGradedCards = settings?.enable_graded_cards ?? false;

  // Image upload (inline edit row — always-on)
  const handleImageUpload = async (cardId, side, file) => {
    const form = new FormData();
    form.append("file", file);
    const endpoint = side === "front" ? "upload-front" : "upload-back";
    try {
      const res = await api.post(`/cards/${cardId}/${endpoint}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const imgField = side === "front" ? "front_image" : "back_image";
      const imgVal = side === "front" ? res.data.front_image : res.data.back_image;
      setCards(prev => prev.map(c => c.id === cardId ? { ...c, [imgField]: imgVal } : c));
      setEditForm(prev => ({ ...prev, [imgField]: imgVal }));
      if (pinnedCard?.id === cardId) setPinnedCard(prev => ({ ...prev, [imgField]: imgVal }));
      showToast(`${side === "front" ? "Front" : "Back"} image uploaded`);
    } catch {
      alert(`Failed to upload ${side} image.`);
    }
  };

  // Delete an uploaded image — calls backend, clears state
  const handleDeleteImage = async (cardId, side) => {
    const endpoint = side === "front" ? "delete-front" : "delete-back";
    try {
      await api.delete(`/cards/${cardId}/${endpoint}`);
      const imgField = side === "front" ? "front_image" : "back_image";
      setCards(prev => prev.map(c => c.id === cardId ? { ...c, [imgField]: null } : c));
      setEditForm(prev => ({ ...prev, [imgField]: null }));
      if (pinnedCard?.id === cardId) setPinnedCard(prev => ({ ...prev, [imgField]: null }));
      showToast(`${side === "front" ? "Front" : "Back"} image deleted`);
    } catch {
      alert(`Failed to delete ${side} image.`);
    }
  };

  // Edit an already-uploaded image — fetch URL → blob → File, then open ImageEditor
  const handleEditExistingImage = async (side) => {
    const url = side === "front" ? selectedCard.front_image : selectedCard.back_image;
    const cardId = selectedCard.id;
    if (!url) return;
    setSelectedCard(null);
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const filename = url.split("/").pop() || "card-photo.jpg";
      const file = new File([blob], filename, { type: blob.type || "image/jpeg" });
      setInlineEditorFile(file);
      setInlineEditorSide(side);
      setInlineEditorCardId(cardId);
      setInlineEditorIsExisting(true);
    } catch {
      alert("Failed to load image for editing.");
    }
  };

  // AI card identification modal (inline add row — gated by enable_image_ai)
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanFile, setScanFile] = useState(null);
  const [scanPreview, setScanPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [pendingScanFile, setPendingScanFile] = useState(null); // scan image to upload after card save

  const handleScanIdentify = async () => {
    if (!scanFile) return;
    setScanning(true);
    setScanError(null);
    const form = new FormData();
    form.append("file", scanFile);
    try {
      const res = await api.post("/cards/identify-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const f = res.data.fields || {};
      const dm = res.data.dictionary_match || {};
      setEditForm(prev => ({
        ...prev,
        ...(f.first_name  ? { first_name:  f.first_name  } : {}),
        ...(f.last_name   ? { last_name:   f.last_name   } : {}),
        ...(f.year        ? { year:        f.year        } : {}),
        ...(f.brand       ? { brand:       f.brand       } : {}),
        ...(f.card_number ? { card_number: f.card_number } : {}),
        ...(dm.rookie != null ? { rookie: dm.rookie ? 1 : 0 } : {}),
        ...(dm.book_high     != null ? { book_high:     dm.book_high }     : {}),
        ...(dm.book_high_mid != null ? { book_high_mid: dm.book_high_mid } : {}),
        ...(dm.book_mid      != null ? { book_mid:      dm.book_mid }      : {}),
        ...(dm.book_low_mid  != null ? { book_low_mid:  dm.book_low_mid }  : {}),
        ...(dm.book_low      != null ? { book_low:      dm.book_low }      : {}),
      }));
      setPendingScanFile(scanFile);
      setShowScanModal(false);
      setScanFile(null);
      setScanPreview(null);
    } catch (err) {
      setScanError(err.response?.data?.detail || "Identification failed. Please try again.");
    } finally {
      setScanning(false);
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  // Fetch the updated card directly so it's always visible regardless of current page
  useEffect(() => {
    if (!returnCardId) { setPinnedCard(null); return; }
    api.get(`/cards/${returnCardId}`)
      .then(res => setPinnedCard(res.data))
      .catch(() => setPinnedCard(null));
  }, [returnCardId]);

  const clearPin = () => { setReturnCardId(null); setPinnedCard(null); setHighlightCardId(null); };

  // Auto-clear the return-from-detail highlight after 10 seconds
  useEffect(() => {
    if (!highlightCardId) return;
    const t = setTimeout(() => setHighlightCardId(null), 10000);
    return () => clearTimeout(t);
  }, [highlightCardId]);
  const clearCloneAnchor = () => { setCloningCardId(null); setCloningParentId(null); setDisplaySnapshot(null); };
  const handlePinRow = (cardId) => {
    setPinnedRowId(prev => {
      const next = prev === cardId ? null : cardId;
      api.put("/settings/", { pinned_card_id: next }).catch(() => {});
      return next;
    });
  };

  // Auto-enter edit mode when navigating back with editCardId in state (e.g. from "Book: never updated" link)
  useEffect(() => {
    if (!autoEditRef.current && returnState.editCardId) {
      const target = pinnedCard?.id === returnState.editCardId
        ? pinnedCard
        : cards.find(c => c.id === returnState.editCardId);
      if (target) {
        handleEditStart(target);
        autoEditRef.current = true;
      }
    }
  }, [cards, pinnedCard]);

  // Focus the scroll container whenever cards load/refresh so arrow keys work immediately.
  // After a delete, scroll the anchor row (row above the deleted one) into view.
  useEffect(() => {
    const el = tableSectionRef.current;
    if (!el) return;
    const savedTop = el.scrollTop;
    el.focus({ preventScroll: true });
    el.scrollTop = savedTop;
  }, [cards]);


  const saveChip = async () => {
    if (!chipEdit) return;
    const { id, field, value } = chipEdit;
    setChipEdit(null);
    const payload = { [field]: field === "price_paid" ? (value !== "" ? parseFloat(value) : null) : (value || null) };
    try {
      const res = await api.put(`/cards/${id}`, payload);
      setCards(prev => prev.map(c => c.id === id ? res.data : c));
      if (pinnedCard?.id === id) setPinnedCard(res.data);
    } catch (err) { console.error("Chip save error:", err); }
  };

  const handlePrintLabel = (card) => {
    setLabelLoading(card.id);
    api.get(`/cards/${card.id}/public`)
      .then((res) => setLabelData(res.data))
      .catch((err) => console.error("Label fetch error:", err))
      .finally(() => setLabelLoading(false));
  };

  const toggleFilterCol = (col) => {
    setOpenFilterCols(prev => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col);
      else next.add(col);
      return next;
    });
  };

  const handleEditStart = (card) => {
    setVariantOpenId(null);
    setVariantForm({});
    setEditingCardId(card.id);
    setEditForm({ ...card });
    origBookVals.current = {
      book_high: card.book_high, book_high_mid: card.book_high_mid,
      book_mid: card.book_mid, book_low_mid: card.book_low_mid, book_low: card.book_low,
    };
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCopyBookValues = (card) => {
    setBookValueClipboard(Object.fromEntries(BOOK_FIELDS.map(f => [f, card[f]])));
  };

  const handlePasteBookValues = async (card) => {
    if (!bookValueClipboard) return;
    try {
      const res = await api.put(`/cards/${card.id}`, { ...card, ...bookValueClipboard });
      setCards(prev => prev.map(c => c.id === card.id ? res.data : c));
      if (pinnedCard?.id === card.id) setPinnedCard(res.data);
      if (card.brand && card.year && card.card_number) {
        try {
          const params = {
            first_name: card.first_name, last_name: card.last_name,
            brand: card.brand, year: card.year, card_number: card.card_number,
            attributes: JSON.stringify(card.card_attributes || {}),
            ...Object.fromEntries(BOOK_FIELDS.filter(f => bookValueClipboard[f]).map(f => [f, bookValueClipboard[f]])),
          };
          const bulkRes = await api.patch("/cards/propagate-book-values", null, { params });
          if (bulkRes.data.updated > 1) {
            showToast(`Book values pasted and updated for all ${bulkRes.data.updated} matching cards`);
            const byId = Object.fromEntries(bulkRes.data.cards.map(c => [c.id, c]));
            setCards(prev => prev.map(c => byId[c.id] ?? c));
          } else {
            showToast("Book values pasted");
          }
        } catch { showToast("Book values pasted"); }
      } else {
        showToast("Book values pasted");
      }
    } catch (err) {
      console.error("Paste book values failed", err);
    }
  };

  const reapplySort = () => {
    clearPin();
    if (sortConfig.length === 0 && settings?.default_sort?.length > 0) {
      setSortConfig(settings.default_sort);
    }
  };

  const handleEditSave = async (cardId) => {  // NOSONAR
    if (cardId === "new") {
      try {
        const res = await api.post("/cards/", editForm);
        const newCard = res.data;
        const newCardId = parseInt(newCard.id, 10);
        // Upload scan image as front photo if one was captured via the camera modal
        if (pendingScanFile && newCardId) {
          const imgForm = new FormData();
          imgForm.append("file", pendingScanFile);
          try {
            const imgRes = await api.post(`/cards/${newCardId}/upload-front`, imgForm, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            newCard.front_image = imgRes.data.front_image;
          } catch { /* non-fatal — card still saved */ }
          setPendingScanFile(null);
        }
        setCards(prev => [newCard, ...prev]);
        setTotal(prev => prev + 1);
        setEditingCardId(null);
        setEditForm({});
        reapplySort();
        setHighlightCardId(newCardId);
        setTimeout(() => setFocusCardId(newCardId), 0);
      } catch (err) {
        console.error("Error adding card:", err);
        alert("Failed to add card.");
      }
    } else {
      try {
        const res = await api.put(`/cards/${cardId}`, editForm);
        setCards(prev => prev.map(c => c.id === cardId ? res.data : c));
        if (pinnedCard?.id === cardId) setPinnedCard(res.data);
        setEditingCardId(null);
        setPinnedRowId(cardId);
        setHighlightCardId(cardId);
        api.put("/settings/", { pinned_card_id: cardId }).catch(() => {});
        // For clone saves the snapshot already keeps the row in place; only
        // trigger a scroll for regular edits where the row may have moved.
        if (cardId !== cloningCardId) setFocusCardId(cardId);

        const bookChanged = BOOK_FIELDS.some(f => (editForm[f] || null) !== (origBookVals.current[f] || null));
        if (bookChanged && !editForm.is_graded && editForm.brand && editForm.year && editForm.card_number) {
          try {
            const params = {
              first_name: editForm.first_name, last_name: editForm.last_name,
              brand: editForm.brand, year: editForm.year, card_number: editForm.card_number,
              attributes: JSON.stringify(editForm.card_attributes || {}),
              ...(editForm.book_high     ? { book_high:     editForm.book_high }     : {}),
              ...(editForm.book_high_mid ? { book_high_mid: editForm.book_high_mid } : {}),
              ...(editForm.book_mid      ? { book_mid:      editForm.book_mid }      : {}),
              ...(editForm.book_low_mid  ? { book_low_mid:  editForm.book_low_mid }  : {}),
              ...(editForm.book_low      ? { book_low:      editForm.book_low }      : {}),
            };
            const bulkRes = await api.patch("/cards/propagate-book-values", null, { params });
            if (bulkRes.data.updated > 1) {
              showToast(`Book values updated for all ${bulkRes.data.updated} matching cards`);
              const byId = Object.fromEntries(bulkRes.data.cards.map(c => [c.id, c]));
              setCards(prev => prev.map(c => byId[c.id] ?? c));
            }
          } catch (bulkErr) {
            console.error("Book value propagation failed:", bulkErr);
          }
        }
      } catch (err) {
        console.error("Error updating card:", err);
        alert("Failed to save changes.");
      }
    }
  };

  const handleEditCancel = () => {
    if (cloningCardId) {
      // Clone in progress — delete the new card and scroll back to parent
      api.delete(`/cards/${cloningCardId}`).catch(console.error);
      setCards(prev => prev.filter(c => c.id !== cloningCardId));
      setTotal(prev => prev - 1);
      setDisplaySnapshot(null);
      setFocusCardId(cloningParentId);
      setCloningCardId(null);
      setCloningParentId(null);
    } else {
      // Regular edit cancel — capture scroll position now and restore after re-render
      const container = tableSectionRef.current;
      const savedTop = container ? container.scrollTop : 0;
      if (container) setTimeout(() => { container.scrollTop = savedTop; }, 0);
    }
    setEditingCardId(null);
    setEditForm({});
    setPendingScanFile(null);
  };

  const openVariant = (card) => {
    setVariantOpenId(card.id);
    setVariantForm(card.card_attributes || {});
  };

  const closeVariant = () => {
    setVariantOpenId(null);
    setVariantForm({});
  };

  const saveVariant = async (cardId) => {
    try {
      const res = await api.put(`/cards/${cardId}`, { card_attributes: variantForm });
      setCards(prev => prev.map(c => c.id === cardId ? res.data : c));
      // Keep editForm in sync — if this card is open for inline editing, its
      // card_attributes would be stale and the next save would revert the variant.
      if (editingCardId === cardId) {
        setEditForm(prev => ({ ...prev, card_attributes: variantForm }));
      }
      closeVariant();
    } catch (err) {
      console.error("Variant save error:", err);
    }
  };

  const handleNewNameKeyDown = (e, field, names) => {
    if (e.key !== "Enter" && e.key !== "Tab") return;
    const typed = (editForm[field] || "").trim().toLowerCase();
    if (!typed) return;
    const match = names.find(n => n.toLowerCase().startsWith(typed));
    if (match && match.toLowerCase() !== typed) {
      if (e.key === "Enter") e.preventDefault();
      setEditForm(prev => ({ ...prev, [field]: match }));
    }
  };

  const handleDuplicate = async (card) => {
    const { id, value, market_factor, front_image, back_image, ...fields } = card;
    try {
      const res = await api.post("/cards/", fields);
      const newCard = res.data;
      skipNextFetchRef.current = true;
      // Insert directly after the original card in the raw cards array
      setCards(prev => {
        const idx = prev.findIndex(c => c.id === card.id);
        if (idx >= 0) {
          const next = [...prev];
          next.splice(idx + 1, 0, newCard);
          return next;
        }
        return [newCard, ...prev];
      });
      setTotal(prev => prev + 1);
      // Freeze the current display order with the new card inserted after its parent
      const parentIdx = displayedCards.findIndex(c => c.id === card.id);
      const snap = displayedCards.map(c => c.id);
      snap.splice(parentIdx >= 0 ? parentIdx + 1 : snap.length, 0, newCard.id);
      setDisplaySnapshot(snap);
      setCloningCardId(newCard.id);
      setCloningParentId(card.id);
      setEditingCardId(newCard.id);
      setEditForm({ ...newCard });
      // Initialise origBookVals so clone save doesn't false-positive bookChanged
      origBookVals.current = {
        book_high: newCard.book_high, book_high_mid: newCard.book_high_mid,
        book_mid: newCard.book_mid, book_low_mid: newCard.book_low_mid, book_low: newCard.book_low,
      };
      setFocusCardId(newCard.id);
    } catch (err) {
      console.error("Duplicate failed:", err);
      alert("Failed to duplicate card.");
    }
  };

  const handleDelete = (card) => {
    const idx = displayedCards.findIndex(c => c.id === card.id);
    // Prefer the card after (takes deleted card's place); fall back to card before
    const anchorId = displayedCards[idx + 1]?.id ?? displayedCards[idx - 1]?.id ?? null;
    deleteAnchorIdRef.current = anchorId;
    setDeleteConfirmCard(card);
  };

  const confirmDelete = async () => {
    const card = deleteConfirmCard;
    const anchorId = deleteAnchorIdRef.current;
    setDeleteConfirmCard(null);
    try {
      await api.delete(`/cards/${card.id}`);
      setCards(prev => prev.filter(c => c.id !== card.id));
      setTotal(prev => prev - 1);
      if (pinnedCard?.id === card.id) setPinnedCard(null);
      if (anchorId) {
        setHighlightCardId(anchorId);
        setTimeout(() => setFocusCardId(anchorId), 0);
      }
    } catch (err) {
      console.error("Error deleting card:", err);
      alert("Failed to delete card.");
    }
  };

  const handleRefreshBook = async (card) => {
    try {
      const res = await api.post(`/cards/${card.id}/refresh-book-values`);
      setCards(prev => prev.map(c => c.id === card.id ? res.data : c));
      if (pinnedCard?.id === card.id) setPinnedCard(res.data);
    } catch (err) {
      console.error("Book refresh failed:", err);
    }
  };

  // Build filter params — uses debounced values so fast typing doesn't trigger mid-word fetches
  const buildFilterParams = React.useCallback(() => ({
    ...(dLastName && { last_name: dLastName }),
    ...(dBrand    && { brand: dBrand }),
    ...(dYear     && { year: Number(dYear) }),
    ...(dGrade    && { grade: dGrade }),
    ...(dGraded !== "all" && { graded: dGraded }),
  }), [dLastName, dBrand, dYear, dGrade, dGraded]);

  const fetchCount = React.useCallback(async () => {
    try {
      const res = await api.get("/cards/count", { params: buildFilterParams() });
      setTotal(res.data.count);
      setTotalValue(res.data.total_value ?? 0);
      if (res.data.count === 0) setLoading(false);
    } catch (err) {
      console.error("Error fetching count:", err);
      setLoading(false);
    }
  }, [buildFilterParams]);

  const fetchCards = React.useCallback(async (skip = 0, append = false) => {
    setLoading(true);
    try {
      const sortParam = sortConfig
        .filter(s => s.key !== "market_factor")
        .map(s => `${s.key}:${s.direction}`)
        .join(",");
      const params = {
        skip,
        limit: FETCH_LIMIT,
        ...buildFilterParams(),
        ...(sortParam && { sort_by: sortParam }),
      };
      const res = await api.get("/cards/", { params });
      if (append) setCards(prev => [...prev, ...res.data]);
      else setCards(res.data);
      setCurrentSkip(skip);
      setHasMore(PAGE_SIZE !== null && res.data.length === FETCH_LIMIT);
    } catch (err) {
      console.error("Error fetching cards:", err);
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, sortConfig]); // eslint-disable-line

  // Load settings once — set settingsLoaded so first card fetch waits for default sort
  useEffect(() => {
    api
      .get("/settings/")
      .then((res) => {
        setSettings(res.data);
        if (res.data.pinned_card_id) setPinnedRowId(res.data.pinned_card_id);
        setSettingsLoaded(true);
      })
      .catch((err) => { console.error("Error fetching settings:", err); setSettingsLoaded(true); });
  }, []);

  // Apply saved default sort on first load (only if no returnState sort was set)
  useEffect(() => {
    if (defaultSortApplied.current) return;
    if (!settings) return;
    defaultSortApplied.current = true;
    if (settings.default_sort?.length > 0 && sortConfig.length === 0) {
      setSortConfig(settings.default_sort);
    }
  }, [settings]);

  // Load player names for smart fill autocomplete
  useEffect(() => {
    api.get("/cards/players")
      .then((res) => {
        const players = res.data.players || [];
        setPlayerNames({
          firstNames: [...new Set(players.map(p => p.first_name))].sort((a, b) => a.localeCompare(b)),
          lastNames: [...new Set(players.map(p => p.last_name))].sort((a, b) => a.localeCompare(b)),
        });
      })
      .catch(() => {});
  }, []);

  // Smart fill for inline new-card row
  useEffect(() => {
    if (editingCardId !== "new") return;
    if (!settings?.enable_smart_fill) return;
    if (!editForm.first_name || !editForm.last_name) return;

    const params = {
      first_name: editForm.first_name.trim().toLowerCase(),
      last_name: editForm.last_name.trim().toLowerCase(),
    };
    if (editForm.brand) params.brand = editForm.brand;
    if (editForm.year && !isNaN(Number(editForm.year))) params.year = Number(editForm.year);

    const timeout = setTimeout(async () => {
      try {
        // Always start with first call (no card_number) to get the canonical card_number
        // for the current name/brand/year. This avoids using a stale card_number from
        // a previous Smart Fill run with a different brand/year.
        const res = await api.get("/cards/smart-fill", { params });
        if (res.data.status === "ok") {
          const f = res.data.fields;
          const filledCardNum = f.card_number || "";
          setEditForm(prev => ({
            ...prev,
            rookie: f.rookie !== undefined ? (f.rookie ? 1 : 0) : prev.rookie,  // NOSONAR
            card_number: filledCardNum || prev.card_number,
          }));
          if (filledCardNum) {
            const res2 = await api.get("/cards/smart-fill", { params: { ...params, card_number: filledCardNum } });
            if (res2.data.status === "ok") {
              const f2 = res2.data.fields;
              setEditForm(prev => ({
                ...prev,
                book_high:     f2.book_high     ?? prev.book_high,
                book_high_mid: f2.book_high_mid ?? prev.book_high_mid,
                book_mid:      f2.book_mid      ?? prev.book_mid,
                book_low_mid:  f2.book_low_mid  ?? prev.book_low_mid,
                book_low:      f2.book_low      ?? prev.book_low,
              }));
            }
          }
        }
      } catch (err) {
        console.error("Smart Fill error:", err);
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [editForm.first_name, editForm.last_name, editForm.brand, editForm.year, editingCardId, settings?.enable_smart_fill]);

  // On mount / navigation back / refresh — reload count and first page
  useEffect(() => {
    if (skipNextFetchRef.current) { skipNextFetchRef.current = false; return; }
    fetchCount();
  }, [location, refreshTick]); // eslint-disable-line

  // Kiosk event listeners
  useEffect(() => {
    const onTableScroll = (e) => {
      if (tableSectionRef.current) tableSectionRef.current.scrollTop += e.detail;
    };
    const onFocusFirstRow = () => {
      if (!tableSectionRef.current) return;
      tableSectionRef.current.scrollTop = 0;
      const firstActionCell = tableSectionRef.current.querySelector('td.action-col.actions-col');
      if (!firstActionCell) return;
      firstActionCell.setAttribute('data-tour', 'kiosk-row-actions');
      // Tag each individual button by title
      const btns = firstActionCell.querySelectorAll('button');
      btns.forEach(btn => {
        const t = btn.getAttribute('title') || '';
        if (t.includes('pin') || t.includes('Pin')) btn.setAttribute('data-tour', 'kiosk-btn-pin');
        else if (t === 'Edit') btn.setAttribute('data-tour', 'kiosk-btn-edit');
        else if (t === 'Copy') btn.setAttribute('data-tour', 'kiosk-btn-copy');
        else if (t.includes('label') || t.includes('Label')) btn.setAttribute('data-tour', 'kiosk-btn-label');
        else if (t === 'Card detail') btn.setAttribute('data-tour', 'kiosk-btn-detail');
        else if (t === 'Delete') btn.setAttribute('data-tour', 'kiosk-btn-delete');
      });
    };
    // Kiosk: open sort modal
    const onOpenSortModal = () => setShowSortModal(true);
    const onCloseSortModal = () => setShowSortModal(false);

    // Kiosk: trigger edit on first visible card row
    // Use refs via rowRefsMap to find the first rendered row's card id
    const onEditFirstRow = () => {
      const ids = Object.keys(rowRefsMap.current);
      if (!ids.length) return;
      // find first card in displayedCards that has a mounted row
      const idSet = new Set(ids.map(Number));
      // We can't access displayedCards here (stale closure) — use the DOM
      const firstTr = tableSectionRef.current?.querySelector('tbody tr[data-card-id]');
      const cardId = firstTr ? Number(firstTr.getAttribute('data-card-id')) : null;
      if (cardId) {
        // Dispatch a targeted event with the card id
        window.dispatchEvent(new CustomEvent("kiosk-edit-card-id", { detail: cardId }));
      }
    };
    const onCancelEdit = () => handleEditCancel();

    // Kiosk: type into new card row field by field (for Smart Fill demo)
    const onTypeNewRowField = (e) => {
      const { field, value } = e.detail;
      setEditForm(prev => ({ ...prev, [field]: value }));
    };

    // Kiosk: open blank new card row (no copy pre-population)
    const onOpenBlankRow = () => {
      setEditForm({ first_name: "", last_name: "", year: "", brand: "", card_number: "", grade: 1.0,
                    rookie: false, book_high: "", book_mid: "", book_low: "", is_graded: false });
      setEditingCardId("new");
    };

    // Kiosk: open new row pre-populated with first card's data (simulates copy)
    const onShowNewRow = () => {
      const firstTr = tableSectionRef.current?.querySelector('tbody tr[data-card-id]');
      const cardId = firstTr ? Number(firstTr.getAttribute('data-card-id')) : null;
      if (cardId) window.dispatchEvent(new CustomEvent("kiosk-populate-copy-row", { detail: cardId }));
      else setEditingCardId("new");
    };
    const onCloseNewRow = () => setEditingCardId(prev => prev === "new" ? null : prev);

    // Kiosk: close label modal
    const onCloseModal = () => setLabelData(null);

    // Kiosk: print label for first card in list (use DOM to avoid stale closure)
    const onPrintFirstLabel = () => {
      const firstTr = tableSectionRef.current?.querySelector('tbody tr[data-card-id]');
      const cardId = firstTr ? Number(firstTr.getAttribute('data-card-id')) : null;
      if (cardId) window.dispatchEvent(new CustomEvent("kiosk-print-label-id", { detail: cardId }));
    };

    const onOpenFilter = (e) => {
      const col = e.detail;
      if (col.endsWith("_close")) {
        const key = col.replace("_close", "");
        setOpenFilterCols(prev => { const next = new Set(prev); next.delete(key); return next; });
      } else {
        setOpenFilterCols(prev => { const next = new Set(prev); next.add(col); return next; });
      }
    };
    const onFilter = (e) => {
      const { field, value } = e.detail;
      if (field === "last_name") setLastNameFilter(value);
      else if (field === "brand") setBrandFilter(value);
      else if (field === "year") setYearFilter(value);
      else if (field === "grade") setGradeFilter(value);
    };
    const onSort = (e) => setSortConfig(e.detail);
    window.addEventListener("kiosk-table-scroll",       onTableScroll);
    window.addEventListener("kiosk-focus-first-row",    onFocusFirstRow);
    window.addEventListener("kiosk-open-filter",        onOpenFilter);
    window.addEventListener("kiosk-filter",             onFilter);
    window.addEventListener("kiosk-sort",               onSort);
    window.addEventListener("kiosk-open-sort-modal",    onOpenSortModal);
    window.addEventListener("kiosk-close-sort-modal",   onCloseSortModal);
    window.addEventListener("kiosk-edit-first-row",     onEditFirstRow);
    window.addEventListener("kiosk-cancel-edit",        onCancelEdit);
    window.addEventListener("kiosk-type-new-row-field",  onTypeNewRowField);
    window.addEventListener("kiosk-open-blank-row",      onOpenBlankRow);
    window.addEventListener("kiosk-show-new-row",        onShowNewRow);
    window.addEventListener("kiosk-close-new-row",      onCloseNewRow);
    window.addEventListener("kiosk-close-modal",        onCloseModal);
    window.addEventListener("kiosk-print-first-label",  onPrintFirstLabel);
    return () => {
      window.removeEventListener("kiosk-table-scroll",      onTableScroll);
      window.removeEventListener("kiosk-focus-first-row",   onFocusFirstRow);
      window.removeEventListener("kiosk-open-filter",       onOpenFilter);
      window.removeEventListener("kiosk-filter",            onFilter);
      window.removeEventListener("kiosk-sort",              onSort);
      window.removeEventListener("kiosk-open-sort-modal",   onOpenSortModal);
      window.removeEventListener("kiosk-close-sort-modal",  onCloseSortModal);
      window.removeEventListener("kiosk-edit-first-row",    onEditFirstRow);
      window.removeEventListener("kiosk-cancel-edit",       onCancelEdit);
      window.removeEventListener("kiosk-type-new-row-field", onTypeNewRowField);
      window.removeEventListener("kiosk-open-blank-row",     onOpenBlankRow);
      window.removeEventListener("kiosk-show-new-row",       onShowNewRow);
      window.removeEventListener("kiosk-close-new-row",     onCloseNewRow);
      window.removeEventListener("kiosk-close-modal",       onCloseModal);
      window.removeEventListener("kiosk-print-first-label", onPrintFirstLabel);
    };
    // eslint-disable-line
  }, []); // eslint-disable-line

  // Kiosk: card-dependent listeners (re-bound when cards/settings change to avoid stale closures)
  useEffect(() => {
    const onEditId = (e) => {
      const card = cards.find(c => c.id === e.detail);
      if (card) handleEditStart(card);
    };
    const onPrintId = (e) => {
      const card = cards.find(c => c.id === e.detail);
      if (card) handlePrintLabel(card);
    };
    const onPopulateCopy = (e) => {
      const card = cards.find(c => c.id === e.detail);
      if (!card) { setEditingCardId("new"); return; }
      setEditForm({
        ...card,
        grade: card.grade != null ? Number(card.grade).toFixed(1) : (settings?.card_grades?.[0] || ""),
        brand: card.brand || settings?.card_makes?.[0] || "",
      });
      setEditingCardId("new");
    };
    window.addEventListener("kiosk-edit-card-id",      onEditId);
    window.addEventListener("kiosk-print-label-id",    onPrintId);
    window.addEventListener("kiosk-populate-copy-row", onPopulateCopy);
    return () => {
      window.removeEventListener("kiosk-edit-card-id",      onEditId);
      window.removeEventListener("kiosk-print-label-id",    onPrintId);
      window.removeEventListener("kiosk-populate-copy-row", onPopulateCopy);
    };
  }, [cards, settings]); // eslint-disable-line

  // Restore focus to open filter input after every render so fetch re-renders don't drop it
  useEffect(() => {
    for (const key of openFilterCols) {
      const el = filterInputRefs.current[key];
      if (el && document.activeElement !== el) el.focus();
    }
  });

  // Fetch first page — wait for settings so default sort is applied before first fetch
  useEffect(() => {
    if (total === 0 || !settingsLoaded) return;
    fetchCards(0);
  }, [total, sortConfig, settings?.cards_per_page, settingsLoaded]); // eslint-disable-line

  // Debounce text filter inputs — 300ms pause before triggering fetch
  useEffect(() => { const t = setTimeout(() => setDLastName(lastNameFilter), 300); return () => clearTimeout(t); }, [lastNameFilter]);
  useEffect(() => { const t = setTimeout(() => setDBrand(brandFilter), 300); return () => clearTimeout(t); }, [brandFilter]);
  useEffect(() => { const t = setTimeout(() => setDGrade(gradeFilter), 300); return () => clearTimeout(t); }, [gradeFilter]);
  useEffect(() => { const t = setTimeout(() => setDYear(yearFilter), 300); return () => clearTimeout(t); }, [yearFilter]);
  useEffect(() => { const t = setTimeout(() => setDGraded(gradedFilter), 300); return () => clearTimeout(t); }, [gradedFilter]);

  // Re-fetch when debounced filters change (count + cards)
  useEffect(() => {
    fetchCount();
  }, [dLastName, dBrand, dYear, dGrade, dGraded]); // eslint-disable-line


  // Sort by market_factor is client-side only (computed field, not in DB)
  const sortedCards = React.useMemo(() => {
    const mfSort = sortConfig.find(s => s.key === "market_factor");
    if (!mfSort) return cards;
    return [...cards].sort((a, b) => {
      const aVal = Number(a.market_factor) || 0;
      const bVal = Number(b.market_factor) || 0;
      if (aVal < bVal) return mfSort.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return mfSort.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [cards, sortConfig]);

  const hasDuplicates = React.useMemo(() => {
    const seen = new Set();
    for (const c of cards) {
      const key = `${(c.first_name || "").toLowerCase()}_${(c.last_name || "").toLowerCase()}_${(c.brand || "").toLowerCase()}_${c.year}_${(c.card_number || "").toLowerCase()}`;
      if (seen.has(key)) return true;
      seen.add(key);
    }
    return false;
  }, [cards]);

  // When a clone session is active, use the frozen snapshot order (updated data only).
  // Otherwise, apply pin-to-top and optional duplicate hiding.
  const displayedCards = React.useMemo(() => {
    if (displaySnapshot) {
      const cardMap = new Map(cards.map(c => [c.id, c]));
      if (pinnedCard) cardMap.set(pinnedCard.id, pinnedCard);
      return displaySnapshot.map(id => cardMap.get(id)).filter(Boolean);
    }

    let result = [...sortedCards];
    if (pinnedCard) {
      const pinId = Number(pinnedCard.id);
      result = [pinnedCard, ...result.filter(c => Number(c.id) !== pinId)];
    }

    if (hideDuplicates) {
      // Score each card to pick the "best" duplicate — higher = better
      const score = (c) => {
        if (c.value) return c.value + 1000;
        if (c.is_graded && c.psa_grade) return c.psa_grade * 10 + 500;
        return (parseFloat(c.grade) || 0) * 100;
      };
      // Group by duplicate key; keep the best per group
      const best = new Map();
      for (const c of cards) {
        const key = `${(c.first_name || "").toLowerCase()}_${(c.last_name || "").toLowerCase()}_${(c.brand || "").toLowerCase()}_${c.year}_${(c.card_number || "").toLowerCase()}`;
        if (!best.has(key) || score(c) > score(best.get(key))) best.set(key, c);
      }
      const keepIds = new Set([...best.values()].map(c => c.id));
      result = result.filter(c => keepIds.has(c.id));
    }

    return result;
  }, [displaySnapshot, cards, sortedCards, pinnedCard, hideDuplicates]);

  const displayedValue = React.useMemo(() => {
    if (!hideDuplicates) return totalValue;
    return displayedCards.reduce((sum, c) => sum + (c.value || 0), 0);
  }, [hideDuplicates, displayedCards, totalValue]);

  // Scroll a specific row into view after render (edit save / clone / cancel)
  useEffect(() => {
    if (!focusCardId) return;
    const el = rowRefsMap.current[focusCardId];
    if (el) {
      // Use instant jump when scrolling to the navigation target on mount (avoids sweep from top)
      const behavior = "instant";
      mountScrollCardIdRef.current = null;
      el.scrollIntoView({ block: "center", behavior });
      setFocusCardId(null);
    }
  }, [focusCardId, displayedCards]);

  const requestSort = (key) => {
    clearPin();
    clearCloneAnchor();
    const existing = sortConfig.find(s => s.key === key);
    const direction = (existing && sortConfig.length === 1 && existing.direction === "asc") ? "desc" : "asc";
    setSortConfig([{ key, direction }]);
  };

  const getSortIndicator = (key) => {
    if (sortConfig.length !== 1 || sortConfig[0].key !== key) return "";
    return sortConfig[0].direction === "asc" ? "▲" : "▼";
  };

  // totalValue comes from the count API (accurate across all filtered cards, not just loaded page)

  const getAnchorIndex = () => {
    const container = tableSectionRef.current;
    if (!container || displayedCards.length === 0) return 0;
    const scrollTop = container.scrollTop;
    for (let i = 0; i < displayedCards.length; i++) {
      const el = rowRefsMap.current[displayedCards[i].id];
      if (el && el.offsetTop >= scrollTop) return i;
    }
    return displayedCards.length - 1;
  };

  const scrollToTop = () => {
    clearCloneAnchor();
    if (tableSectionRef.current) tableSectionRef.current.scrollTop = 0;
  };

  const scrollToBottom = () => {
    clearCloneAnchor();
    if (tableSectionRef.current) tableSectionRef.current.scrollTop = tableSectionRef.current.scrollHeight;
  };

  const jumpUp = () => {
    if (displayedCards.length === 0) return;
    clearCloneAnchor();
    const targetIdx = Math.max(0, getAnchorIndex() - jumpRate);
    setFocusCardId(displayedCards[targetIdx].id);
  };

  const jumpDown = () => {
    if (displayedCards.length === 0) return;
    clearCloneAnchor();
    const targetIdx = Math.min(displayedCards.length - 1, getAnchorIndex() + jumpRate);
    setFocusCardId(displayedCards[targetIdx].id);
  };

    return (
      <>
      <AppHeader />
      <div className="list-container">

        {/* Line 1: compact title */}
        <h2 className="page-header" style={{ textAlign: "center", margin: "0.5rem 0 0.25rem" }}>
          Cards
        </h2>

        {/* Line 2: single toolbar — left / center / right */}
        <div style={{ display: "flex", alignItems: "center", padding: "0 1rem", marginBottom: "0.5rem" }}>

          {/* Left: selection toggle + print selected */}
          <div style={{ flex: 1, display: "flex", gap: "0.5rem" }}>
            {tierAllowsLabels && (!selectionMode ? (
              <button
                className="nav-btn"
                style={{ padding: "0.3rem 0.9rem", fontSize: "0.85rem" }}
                onClick={() => setSelectionMode(true)}
              >
                Select to Print
              </button>
            ) : (
              <button
                className="nav-btn secondary"
                style={{ padding: "0.3rem 0.9rem", fontSize: "0.85rem" }}
                onClick={() => { setSelectionMode(false); setSelectedIds(new Set()); tableSectionRef.current?.focus(); }}
              >
                ✕ Cancel
              </button>
            ))}
            {tierAllowsLabels && selectionMode && selectedIds.size > 0 && (
              <button
                className="nav-btn"
                style={{ padding: "0.3rem 0.9rem", fontSize: "0.85rem" }}
                onClick={() => navigate("/batch-labels", { state: { mode: "selection", ids: [...selectedIds] } })}
              >
                🖨️ Print Selected ({selectedIds.size})
              </button>
            )}
          </div>

          {/* Center: count [clear filters] · value */}
          <div data-tour="toolbar-count" style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.9rem", color: "#555" }}>
            {hideDuplicates
              ? <span><span style={{ color: "#dc3545" }}>{displayedCards.length}</span><span style={{ color: "var(--accent-blue)" }}> of {total} cards (dupes hidden)</span></span>
              : (lastNameFilter || brandFilter || yearFilter || gradeFilter || gradedFilter !== "all")
              ? <>
                  <span><span style={{ color: "#dc3545" }}>{total}</span><span style={{ color: "var(--accent-blue)" }}> cards (filtered)</span></span>
                  <span
                    role="button"  // NOSONAR
                    tabIndex={0}
                    onClick={() => { setLastNameFilter(""); setBrandFilter(""); setYearFilter(""); setGradeFilter(""); setGradedFilter("all"); clearCloneAnchor(); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { setLastNameFilter(""); setBrandFilter(""); setYearFilter(""); setGradeFilter(""); setGradedFilter("all"); clearCloneAnchor(); } }}
                    style={{ color: "#dc3545", cursor: "pointer", fontSize: "0.85rem", textDecoration: "underline" }}
                  >✕ Clear filters</span>
                </>
              : hasMore
              ? <span style={{ color: "var(--accent-blue)" }}>{cards.length} of {total} cards</span>
              : <span style={{ color: "var(--accent-blue)" }}>{total} cards</span>
            }
            {hasMore && !loading && (
              <span
                role="button" tabIndex={0}
                onClick={() => fetchCards(currentSkip + FETCH_LIMIT, true)}
                onKeyDown={e => e.key === "Enter" && fetchCards(currentSkip + FETCH_LIMIT, true)}
                title="Load more cards"
                style={{ color: "var(--accent-blue)", cursor: "pointer", fontSize: "1.1rem", lineHeight: 1, userSelect: "none" }}
              >＋</span>
            )}
            <span>&middot;</span>
            <span style={{ color: "#2e7d32" }} data-tip="Total calculated value of your current collection view — based on book values and grade factors">Value: {fmtDollar(displayedValue)}</span>
          </div>

          {/* Right: sort + print buttons */}
          <div style={{ flex: 1, display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            {(hasDuplicates || hideDuplicates) && (
              <button
                className="nav-btn"
                onClick={() => setHideDuplicates(h => !h)}
                style={{ padding: "0.3rem 0.9rem", fontSize: "0.85rem",
                  background: hideDuplicates ? "#28a745" : "#dc3545",
                  color: "#fff", border: "none" }}
                title={hideDuplicates ? "Show all duplicates" : "Hide duplicates — show best only"}
              >
                {hideDuplicates ? "Show Dupes" : "Hide Dupes"}
              </button>
            )}
            <button
              data-tour="sort-btn"
              className="nav-btn"
              onClick={() => setShowSortModal(true)}
              style={{ padding: "0.3rem 0.9rem", fontSize: "0.85rem", ...(sortConfig.length > 0 && { background: "#1a7a1a" }) }}
              title="Advanced sort"
            >
              ⇅ Sort{sortConfig.length > 0 ? ` (${sortConfig.length})` : ""}
            </button>
            {tierAllowsLabels && (
              <button
                className="nav-btn"
                style={{ padding: "0.3rem 0.9rem", fontSize: "0.85rem" }}
                onClick={() => navigate("/batch-labels", { state: { mode: "all" } })}
              >
                🖨️ Print All
              </button>
            )}
          </div>

        </div>

        <div style={{ clear: "both" }} />  {/* ensures layout resets after float */}
      
        {loading ? (
          <div style={{ textAlign: "center", marginTop: "3rem" }}>
            <div className="cs-spinner" />
            <p style={{ color: "var(--text-muted)", marginTop: "1rem", fontSize: "0.95rem" }}>Loading cards…</p>
          </div>
        ) : (
          <div ref={tableSectionRef} tabIndex={-1} className="card-section" style={{ width: "100%", boxSizing: "border-box", overflow: "auto", maxHeight: "calc(100vh - 180px)", outline: "none" }}>
          {/* Table */}
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {selectionMode && (
                    <th style={{ width: 32, textAlign: "center", padding: "0 4px" }}>
                      <input
                        type="checkbox"
                        title="Select all"
                        checked={displayedCards.length > 0 && displayedCards.every(c => selectedIds.has(c.id))}
                        onChange={(e) => {
                          const ids = displayedCards.map(c => c.id);
                          setSelectedIds(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) ids.forEach(id => next.add(id));
                            else ids.forEach(id => next.delete(id));
                            return next;
                          });
                        }}
                      />
                    </th>
                  )}
                  <th className="fname-col" data-tip="Player first name">First</th>
                  <th className="lname-col" data-tour="col-last" onClick={() => requestSort("last_name")} style={{ cursor: "pointer" }} data-tip="Player last name — click to sort · click 🔍 to filter">
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}>
                      Last {getSortIndicator("last_name")}
                      <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); toggleFilterCol("last"); }} onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); toggleFilterCol("last"); } }} style={{ cursor: "pointer", fontSize: "0.75rem", color: lastNameFilter ? "#007bff" : "#aaa" }} title="Filter by last name">🔍</span>  {/* NOSONAR */}
                    </div>
                    {openFilterCols.has("last") && (
                      <input ref={el => filterInputRefs.current.last = el} type="text" value={lastNameFilter} onChange={(e) => setLastNameFilter(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.key === "Escape" && toggleFilterCol("last")} placeholder="Filter..." style={{ width: "90%", fontSize: "0.75rem", padding: "1px 4px", display: "block", margin: "3px auto 0" }} />
                    )}
                  </th>
                  <th className="year-col" onClick={() => requestSort("year")} style={{ cursor: "pointer" }} data-tip="Year the card was produced — click to sort · click 🔍 to filter">
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}>
                      Year {getSortIndicator("year")}
                      <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); toggleFilterCol("year"); }} onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); toggleFilterCol("year"); } }} style={{ cursor: "pointer", fontSize: "0.75rem", color: yearFilter ? "#007bff" : "#aaa" }} title="Filter by year">🔍</span>  {/* NOSONAR */}
                    </div>
                    {openFilterCols.has("year") && (
                      <input ref={el => filterInputRefs.current.year = el} type="text" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.key === "Escape" && toggleFilterCol("year")} placeholder="Filter..." style={{ width: "90%", fontSize: "0.75rem", padding: "1px 4px", display: "block", margin: "3px auto 0" }} />
                    )}
                  </th>
                  <th className="brand-col" data-tip="Card manufacturer — e.g. Topps, Bowman, Fleer, Upper Deck · click 🔍 to filter">
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}>
                      Brand
                      <span role="button" tabIndex={0} onClick={() => toggleFilterCol("brand")} onKeyDown={(e) => { if (e.key === "Enter") toggleFilterCol("brand"); }} style={{ cursor: "pointer", fontSize: "0.75rem", color: brandFilter ? "#007bff" : "#aaa" }} title="Filter by brand">🔍</span>  {/* NOSONAR */}
                    </div>
                    {openFilterCols.has("brand") && (
                      <input ref={el => filterInputRefs.current.brand = el} type="text" value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} onKeyDown={(e) => e.key === "Escape" && toggleFilterCol("brand")} placeholder="Filter..." style={{ width: "90%", fontSize: "0.75rem", padding: "1px 4px", display: "block", margin: "3px auto 0" }} />
                    )}
                  </th>
                  <th className="card-number-col" data-tip="The card number as printed on the card">Card #</th>
                  <th className="rookie-col" style={{ textAlign: "center", width: 55 }} data-tip="Rookie card — the player's first major-brand card appearance">Rookie</th>

                  <th className="grade-col" data-tour="col-grade" style={{ textAlign: "center", width: 65, cursor: "pointer" }} data-tip="Condition grade — 3.0 NM-MT+, 1.5 NM, 1.0 EX, 0.8 VG, 0.4 FR, 0.2 PR — click to sort · click 🔍 to filter" onClick={() => requestSort("grade")}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}>
                      Grade {getSortIndicator("grade")}
                      <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); toggleFilterCol("grade"); }} onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); toggleFilterCol("grade"); } }} style={{ cursor: "pointer", fontSize: "0.75rem", color: gradeFilter ? "#007bff" : "#aaa" }} title="Filter by grade">🔍</span>  {/* NOSONAR */}
                    </div>
                    {openFilterCols.has("grade") && (
                      <input ref={el => filterInputRefs.current.grade = el} type="text" value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.key === "Escape" && toggleFilterCol("grade")} placeholder="Filter..." style={{ width: "90%", fontSize: "0.75rem", padding: "1px 4px", display: "block", margin: "3px auto 0" }} />
                    )}
                    {enableGradedCards && openFilterCols.has("grade") && (
                      <div style={{ display: "flex", gap: "2px", justifyContent: "center", marginTop: "3px" }} onClick={e => e.stopPropagation()}>
                        {["all","PSA","SGC","BGS","raw"].map(opt => {
                          const val = opt === "all" ? "all" : opt === "raw" ? "raw" : opt.toLowerCase();
                          return (
                            <button key={opt} onClick={() => setGradedFilter(val)}
                              style={{ padding: "1px 3px", fontSize: "0.6rem", borderRadius: 3,
                                border: "1px solid #ccc", cursor: "pointer", whiteSpace: "nowrap",
                                background: gradedFilter === val ? "#1976d2" : "transparent",
                                color: gradedFilter === val ? "#fff" : "inherit" }}>
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </th>

                  <th className="book-col" data-tour="col-book" style={{ textAlign: "center", minWidth: 180 }} data-tip="Book values — the reference price for this card across five tiers: High, High-Mid, Mid, Low-Mid, Low">
                    Book
                    <button
                      onClick={() => { setBookEditMode(m => !m); if (bookEditMode) setBookValueClipboard(null); }}
                      title={bookEditMode ? "Exit book value copy mode" : "Enter book value copy mode"}
                      style={{ position: "absolute", bottom: 3, right: 5, background: "none", border: "none",
                        cursor: "pointer", fontSize: "0.55rem", lineHeight: 1, padding: 0,
                        color: bookEditMode ? "#0891b2" : "#9ca3af" }}
                    >✏️</button>
                  </th>
                  {isAdmin && <th className="market-factor-col" style={{ textAlign: "center", width: 80 }}>Market Factor</th>}

                  <th className="card-value-col" data-tour="col-value" style={{ textAlign: "center", cursor: "pointer" }}
                      data-tip="Calculated market value — derived from book values, card condition, and CardStoard market factoring and formulas"
                      onClick={() => requestSort("card_value")}>Card Value {getSortIndicator("card_value")}
                  </th>

                  <th className="card-images-col" style={{ textAlign: "center", width: 65 }} data-tip="Front and back card photos — click the image icon to view">Images</th>
                  <th className="action-col actions-col" style={{ textAlign: "center", width: 140 }}>
                    {/* Row 1: |◄ ▲ [rate] ▼ ►| */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "1.2rem", marginBottom: "0.25rem" }}>
                      <button onClick={scrollToTop} disabled={displayedCards.length === 0}
                        style={{ background: "none", border: "none", padding: "2px 3px", fontSize: "0.85rem", width: "auto",
                                 cursor: displayedCards.length === 0 ? "default" : "pointer",
                                 opacity: displayedCards.length === 0 ? 0.25 : 1, color: "var(--text-secondary)" }}
                        title="Jump to top">|◄</button>
                      <button onClick={jumpUp} disabled={displayedCards.length === 0}
                        style={{ background: "none", border: "none", padding: "2px 3px", fontSize: "0.85rem", width: "auto",
                                 cursor: displayedCards.length === 0 ? "default" : "pointer",
                                 opacity: displayedCards.length === 0 ? 0.25 : 1, color: "var(--text-secondary)" }}
                        title={`Page up ${jumpRate} rows`}>▲</button>
                      <select value={jumpRate} onChange={e => setJumpRate(Number(e.target.value))}
                        style={{ fontSize: "0.75rem", padding: "1px 2px", border: "1px solid var(--border)",
                                 background: "var(--bg-input)", color: "var(--text-primary)",
                                 borderRadius: 4, cursor: "pointer", width: "auto" }}
                        title="Page size (rows per step)">
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={250}>250</option>
                      </select>
                      <button onClick={jumpDown} disabled={displayedCards.length === 0}
                        style={{ background: "none", border: "none", padding: "2px 3px", fontSize: "0.85rem", width: "auto",
                                 cursor: displayedCards.length === 0 ? "default" : "pointer",
                                 opacity: displayedCards.length === 0 ? 0.25 : 1, color: "var(--text-secondary)" }}
                        title={`Page down ${jumpRate} rows`}>▼</button>
                      <button onClick={scrollToBottom} disabled={displayedCards.length === 0}
                        style={{ background: "none", border: "none", padding: "2px 3px", fontSize: "0.85rem", width: "auto",
                                 cursor: displayedCards.length === 0 ? "default" : "pointer",
                                 opacity: displayedCards.length === 0 ? 0.25 : 1, color: "var(--text-secondary)" }}
                        title="Jump to bottom">►|</button>
                    </div>
                    {/* Row 2: pin centered under left cluster, add centered under right cluster */}
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                        <button onClick={() => { if (pinnedRowId) { setFocusCardId(pinnedRowId); setHighlightCardId(pinnedRowId); } }} disabled={!pinnedRowId}
                          style={{ background: "none", border: "none", padding: 0, fontSize: "1.1rem", width: "auto",
                                   cursor: pinnedRowId ? "pointer" : "default",
                                   opacity: pinnedRowId ? 1 : 0.5, color: "#ff0000" }}
                          title={pinnedRowId ? "Jump to pinned row" : "No row pinned"}>📌</button>
                      </div>
                      <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                        <button
                          onClick={() => {
                            if (editingCardId === "new") return;
                            setEditingCardId("new");
                            if (tableSectionRef.current) tableSectionRef.current.scrollTop = 0;
                            setEditForm({
                              first_name: "", last_name: "", year: "", card_number: "",
                              brand: settings?.card_makes?.[0] || "",
                              grade: settings?.card_grades?.[0] || "",
                              rookie: 0,
                              book_high: "", book_high_mid: "", book_mid: "", book_low_mid: "", book_low: "",
                              is_graded: false, psa_grade: null, grading_company: null, value: null
                            });
                          }}
                          data-tour="add-card-btn"
                          style={{ background: "none", border: "none", fontSize: "1.5rem", width: "auto", padding: 0,
                                   cursor: editingCardId === "new" ? "not-allowed" : "pointer",
                                   color: editingCardId === "new" ? "#aaa" : "#28a745" }}
                          title="Add card">＋</button>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {editingCardId === "new" && (() => {
                  const inp = { fontSize: "0.8rem", padding: "2px 4px", width: "100%", boxSizing: "border-box", borderRadius: "4px", border: "1px solid #bbb" };
                  return (
                    <tr style={{ backgroundColor: "#f0fff4", outline: "2px solid #28a745" }}>
                      {selectionMode && <td style={{ width: 32 }} />}
                      <td className="fname-col"><input style={inp} placeholder="First" value={editForm.first_name || ""} onChange={e => handleEditChange("first_name", e.target.value)} onKeyDown={e => handleNewNameKeyDown(e, "first_name", playerNames.firstNames)} /></td>
                      <td className="lname-col"><input style={inp} placeholder="Last" value={editForm.last_name || ""} onChange={e => handleEditChange("last_name", e.target.value)} onKeyDown={e => handleNewNameKeyDown(e, "last_name", playerNames.lastNames)} /></td>
                      <td className="year-col"><input style={inp} type="number" placeholder="Year" value={editForm.year || ""} onChange={e => handleEditChange("year", e.target.value)} /></td>
                      <td className="brand-col">
                        <select style={inp} value={editForm.brand || ""} onChange={e => handleEditChange("brand", e.target.value)}>
                          {(settings?.card_makes || []).map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </td>
                      <td className="card-number-col"><input style={inp} placeholder="Card #" value={editForm.card_number || ""} onChange={e => handleEditChange("card_number", e.target.value)} /></td>
                      <td className="rookie-col" style={{ textAlign: "center" }}>
                        <input type="checkbox" checked={!!Number(editForm.rookie)} onChange={e => handleEditChange("rookie", e.target.checked ? 1 : 0)} />
                      </td>
                      <td className="grade-col">
                        {enableGradedCards && (
                          <div style={{ display: "flex", justifyContent: "center", gap: "3px", marginBottom: "4px" }}>
                            {["Raw", "Graded"].map(mode => (
                              <button key={mode}
                                onClick={() => handleEditChange("is_graded", mode === "Graded")}
                                style={{ padding: "1px 6px", fontSize: "0.7rem", borderRadius: 3,
                                  border: "1px solid #ccc", cursor: "pointer",
                                  background: (editForm.is_graded ? "Graded" : "Raw") === mode ? "#1976d2" : "transparent",
                                  color: (editForm.is_graded ? "Graded" : "Raw") === mode ? "#fff" : "inherit" }}>
                                {mode}
                              </button>
                            ))}
                          </div>
                        )}
                        {editForm.is_graded
                          ? <>
                              <select style={{ ...inp, marginBottom: "2px" }}
                                value={editForm.grading_company || ""}
                                onChange={e => handleEditChange("grading_company", e.target.value)}>
                                <option value="">Co.</option>
                                {["PSA","SGC","BGS"].map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                              <select style={{ ...inp, marginBottom: "2px" }}
                                value={editForm.psa_grade || ""}
                                onChange={e => handleEditChange("psa_grade", parseInt(e.target.value, 10))}>
                                <option value="">Gr.</option>
                                {[10,9,8,7,6,5,4,3,2,1].map(n => <option key={n} value={n}>{n}</option>)}
                              </select>
                              <input style={inp} placeholder="Serial #" maxLength={20}
                                value={editForm.serial_number || ""}
                                onChange={e => handleEditChange("serial_number", e.target.value || null)} />
                            </>
                          : <select style={inp} value={editForm.grade || ""} onChange={e => handleEditChange("grade", parseFloat(e.target.value))}>
                              {(settings?.card_grades || []).map(gr => <option key={gr} value={gr}>{gr}</option>)}
                            </select>
                        }
                      </td>
                      <td className="book-col">
                        {editForm.is_graded
                          ? <span style={{ fontSize: "0.75rem", color: "#9ca3af", fontStyle: "italic" }}>—</span>
                          : <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                              {[["book_high","H","High (NM-MT+)"],["book_high_mid","HM","High Mid (NM)"],["book_mid","M","Mid (EX)"],["book_low_mid","LM","Low Mid (VG)"],["book_low","L","Low (PR)"]].map(([field, label, title]) => (
                                <div key={field} style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                                  <span style={{ fontSize: "0.7rem", color: "#888", width: "18px", flexShrink: 0 }}>{label}</span>
                                  <input style={inp} type="number" title={title} value={editForm[field] || ""} onChange={e => handleEditChange(field, e.target.value)} />
                                </div>
                              ))}
                            </div>
                        }
                      </td>
                      {isAdmin && <td className="market-factor-col" />}
                      <td className="value-col">
                        {editForm.is_graded && (
                          <input style={{ ...inp, width: 70 }} type="number" placeholder="Market $"
                            value={editForm.value ?? ""}
                            onChange={e => handleEditChange("value", e.target.value === "" ? null : parseFloat(e.target.value))} />
                        )}
                      </td>
                      <td className="image-col" style={{ textAlign: "center" }}>
                        {settings?.enable_image_ai && (
                          <button
                            onClick={() => { setShowScanModal(true); setScanFile(null); setScanPreview(null); setScanError(null); }}
                            title="Identify card from photo"
                            style={{ background: "none", border: "1px solid #1976d2", borderRadius: 6, padding: "3px 6px", cursor: "pointer", fontSize: "1rem", color: "#1976d2" }}
                          >📷</button>
                        )}
                      </td>
                      <td className="action-col actions-col" style={{ textAlign: "center" }}>
                        <div style={{ display: "flex", gap: "0.4rem", justifyContent: "center" }}>
                          <button onClick={() => handleEditSave("new")} style={{ background: "#28a745", color: "white", border: "none", borderRadius: "4px", padding: "4px 10px", cursor: "pointer", fontSize: "0.85rem", fontWeight: "bold", width: "auto" }}>✓ Save</button>
                          <button onClick={handleEditCancel} style={{ background: "#6c757d", color: "white", border: "none", borderRadius: "4px", padding: "4px 10px", cursor: "pointer", fontSize: "0.85rem", fontWeight: "bold", width: "auto" }}>✗ Cancel</button>
                        </div>
                      </td>
                    </tr>
                  );
                })()}  {/* NOSONAR */}
                {displayedCards.map((card) => {
                  const g = parseFloat(card.grade);
                  const rookieVal = card.rookie === "*" || card.rookie === "1" || Number(card.rookie) === 1 || card.rookie === true;
                  const isEditing = editingCardId === card.id;

                  let gradeClass = "grade-unknown";
                  if (!Number.isNaN(g)) {
                    if (g === 3) gradeClass = "grade-mt";
                    else if (g === 1.5) gradeClass = "grade-ex";
                    else if (g === 1) gradeClass = "grade-vg";
                    else if (g === 0.8) gradeClass = "grade-gd";
                    else if (g === 0.4) gradeClass = "grade-fr";
                    else gradeClass = "grade-pr";
                  }

                  const inp = { fontSize: "0.8rem", padding: "2px 4px", width: "100%", boxSizing: "border-box", borderRadius: "4px", border: "1px solid #bbb" };

                  return (
                    <tr
                      key={card.id}
                      data-card-id={card.id}
                      ref={el => { if (el) rowRefsMap.current[card.id] = el; else delete rowRefsMap.current[card.id]; }}
                      style={(() => {
                        if (isEditing) return { backgroundColor: "#f0f7ff", outline: "2px solid #1976d2" };
                        if (selectedIds.has(card.id)) return { backgroundColor: "#dceeff" };

                        // Compute base row color from rookie/grade rules
                        const isDark = document.documentElement.getAttribute("data-theme") === "dark";
                        const def = isDark
                          ? { rg3: "#b8ad00", g3: "#5f3d96", r: "#1d6090" }
                          : { rg3: "#fff3c4", g3: "#e8dcff", r: "#b8d8f7" };
                        const isPremiumGrade = card.is_graded ? card.psa_grade >= 9 : g === 3;
                        let base = {};
                        if (rookieVal && isPremiumGrade)
                          base = { backgroundColor: isDark ? def.rg3 : (settings?.row_color_rookie_grade3 || def.rg3) };
                        else if (isPremiumGrade)
                          base = { backgroundColor: isDark ? def.g3 : (settings?.row_color_grade3 || def.g3) };
                        else if (rookieVal)
                          base = { backgroundColor: isDark ? def.r : (settings?.row_color_rookie || def.r) };

                        // Yellow highlight overlays base color — keeps background, adds outline only
                        if (Number(card.id) === Number(returnCardId) || Number(card.id) === Number(highlightCardId))
                          return { ...base, outline: "2px solid #ffc107", transition: "background-color 0.5s" };

                        return base;
                      })()}
                    >
                      {/* Checkbox — only in selection mode */}
                      {selectionMode && (
                        <td style={{ width: 32, textAlign: "center", padding: "0 4px" }}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(card.id)}
                            onChange={() => {
                              setSelectedIds(prev => {
                                const next = new Set(prev);
                                if (next.has(card.id)) next.delete(card.id);
                                else next.add(card.id);
                                return next;
                              });
                            }}
                          />
                        </td>
                      )}
                      {/* First */}
                      <td className="fname-col">
                        {isEditing
                          ? <input style={inp} value={editForm.first_name || ""} onChange={e => handleEditChange("first_name", e.target.value)} />
                          : <span>{card.first_name}</span>}
                      </td>

                      {/* Last */}
                      <td className="lname-col">
                        {isEditing
                          ? <input style={inp} value={editForm.last_name || ""} onChange={e => handleEditChange("last_name", e.target.value)} />
                          : card.last_name}
                      </td>

                      {/* Year */}
                      <td className="year-col">
                        {isEditing
                          ? <input style={inp} type="number" value={editForm.year || ""} onChange={e => handleEditChange("year", e.target.value)} />
                          : card.year}
                      </td>

                      {/* Brand — click to expand variant accordion */}
                      <td className="brand-col" style={(() => {
                        const a = card.card_attributes || {};
                        const customVars = settings?.custom_variants ?? [];
                        const hasExtra = !isEditing && (variantOpenId === card.id || a.parallel || a.refractor || a.autograph || a.short_print || a.numbered || a.traded || a.subset || a.artists_proof || a.press_proof || a.gold || a.tiffany || a.update || a.error || a.nno || customVars.some(cv => a[cv.key]));
                        return hasExtra ? { verticalAlign: "top" } : { verticalAlign: "middle", textAlign: "center" };
                      })()}>
                        {isEditing
                          ? <select style={inp} value={editForm.brand || ""} onChange={e => handleEditChange("brand", e.target.value)}>
                              {(settings?.card_makes || []).map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                          : <>
                              <span
                                className="badge badge-brand"
                                role="button"  // NOSONAR
                                tabIndex={0}
                                style={{ cursor: "pointer", userSelect: "none" }}
                                onClick={() => variantOpenId === card.id ? closeVariant() : openVariant(card)}
                                onKeyDown={(e) => { if (e.key === "Enter") { variantOpenId === card.id ? closeVariant() : openVariant(card); } }}
                                title="Click to view/edit card variants"
                              >{card.brand}</span>
                              {/* Variant chips — shown when collapsed and attributes set */}
                              {variantOpenId !== card.id && (() => {
                                const a = card.card_attributes || {};
                                const cs = { display: "inline-block", fontSize: "0.65rem", color: "#fff", borderRadius: 10, padding: "1px 5px", marginLeft: 3 };
                                const chips = [
                                  a.parallel    && <span key="par" style={{ ...cs, background: "#7c3aed" }}>{a.parallel}</span>,
                                  a.refractor   && <span key="ref" style={{ ...cs, background: "#0ea5e9" }}>Refractor</span>,
                                  a.autograph   && <span key="auto" style={{ ...cs, background: "#dc2626" }}>{a.authenticator ? `Auto/${a.authenticator}` : "Autograph"}</span>,
                                  a.short_print && <span key="sp"  style={{ ...cs, background: "#b45309" }}>Short Print</span>,
                                  a.numbered    && <span key="num" style={{ ...cs, background: "#374151" }}>/{a.numbered}</span>,
                                  a.traded      && <span key="tr"  style={{ ...cs, background: "#065f46" }}>Traded</span>,
                                  a.subset      && <span key="sub" style={{ ...cs, background: "#6b7280" }}>{a.subset}</span>,
                                  a.artists_proof && <span key="ap" style={{ ...cs, background: "#7e22ce" }}>Artist's Proof</span>,
                                  a.press_proof && <span key="pp"  style={{ ...cs, background: "#0f766e" }}>Press Proof</span>,
                                  a.gold        && <span key="gld" style={{ ...cs, background: "#a16207" }}>Gold</span>,
                                  a.tiffany     && <span key="tif" style={{ ...cs, background: "#0e7490" }}>Tiffany</span>,
                                  a.update      && <span key="upd" style={{ ...cs, background: "#4f46e5" }}>Update</span>,
                                  a.error       && <span key="err" style={{ ...cs, background: "#b91c1c" }}>Error</span>,
                                  a.nno         && <span key="nno" style={{ ...cs, background: "#78350f" }}>No Number On</span>,
                                  ...(settings?.custom_variants ?? []).filter(cv => a[cv.key]).map(cv =>
                                    <span key={cv.key} style={{ ...cs, background: "#475569" }}>{cv.label}</span>
                                  ),
                                ].filter(Boolean);
                                return chips.length > 0 ? <div style={{ marginTop: 3 }}>{chips}</div> : null;
                              })()}
                              {/* Variant accordion panel */}
                              {variantOpenId === card.id && (
                                <div
                                  style={{ marginTop: "6px", textAlign: "left", background: "var(--bg-input, #f8f8f8)", border: "1px solid var(--border-color, #ddd)", borderRadius: 6, padding: "8px 10px", minWidth: 240 }}
                                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); saveVariant(card.id); } }}
                                >
                                  {[
                                    ["parallel",    "Parallel",   "text",     "e.g. Gold Prizm"],
                                    ["numbered",    "Numbered",   "text",     "e.g. 100"],
                                    ["subset",      "Subset",     "text",     "e.g. Future Stars"],
                                  ].map(([key, label, type, ph]) => (
                                    <div key={key} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                                      <label style={{ fontSize: "0.72rem", width: 62, color: "var(--text-muted)", flexShrink: 0 }}>{label}</label>
                                      <input
                                        type={type}
                                        placeholder={ph}
                                        value={variantForm[key] || ""}
                                        onChange={e => setVariantForm(f => ({ ...f, [key]: e.target.value || undefined }))}
                                        style={{ fontSize: "0.78rem", padding: "2px 4px", border: "1px solid var(--border-color, #ccc)", borderRadius: 3, width: "100%", background: "var(--bg-input, #fff)" }}
                                      />
                                    </div>
                                  ))}
                                  {(() => {
                                    const ALL_VARIANTS = [
                                      ["refractor",    "Ref",  "Refractor"],
                                      ["short_print",  "SP",   "Short Print"],
                                      ["autograph",    "Auto", "Autograph"],
                                      ["traded",       "Trd",  "Traded"],
                                      ["artists_proof","AP",   "Artist's Proof"],
                                      ["press_proof",  "PP",   "Press Proof"],
                                      ["gold",         "Gld",  "Gold"],
                                      ["tiffany",      "Tiff", "Tiffany"],
                                      ["update",       "Upd",  "Update"],
                                      ["error",        "Err",  "Error"],
                                      ["nno",          "NNO",  "No Number On"],
                                    ];
                                    const enabled = settings?.card_variants ?? ALL_VARIANTS.map(v => v[0]);
                                    const visible = ALL_VARIANTS.filter(([key]) => enabled.includes(key));
                                    const customVars = settings?.custom_variants ?? [];
                                    const allVisible = [
                                      ...visible.map(([key, abbr, full]) => ({ key, abbr, full })),
                                      ...customVars.map(cv => ({ key: cv.key, abbr: cv.abbr, full: cv.label })),
                                    ];
                                    return (
                                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "2px 6px" }}>
                                        {allVisible.map(({ key, abbr, full }) => (
                                          <label key={key} title={full} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.75rem", cursor: "pointer", whiteSpace: "nowrap" }}>
                                            <input
                                              type="checkbox"
                                              checked={!!variantForm[key]}
                                              onChange={e => setVariantForm(f => ({ ...f, [key]: e.target.checked || undefined }))}
                                            />
                                            {abbr}
                                          </label>
                                        ))}
                                      </div>
                                    );
                                  })()}
                                  {variantForm.autograph && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 6 }}>
                                      <label style={{ fontSize: "0.72rem", color: "var(--text-muted)", flexShrink: 0 }}>Auth</label>
                                      <select
                                        value={variantForm.authenticator || ""}
                                        onChange={e => setVariantForm(f => ({ ...f, authenticator: e.target.value || undefined }))}
                                        style={{ fontSize: "0.78rem", padding: "2px 4px", border: "1px solid var(--border)", borderRadius: 3, background: "var(--bg-input)", color: "var(--text-primary)", flex: 1 }}>
                                        <option value="">— Select —</option>
                                        {["JSA","PSA","SGC","BKT"].map(a => <option key={a} value={a}>{a}</option>)}
                                      </select>
                                    </div>
                                  )}
                                  <div style={{ display: "flex", gap: 5, marginTop: 6 }}>
                                    <button onClick={() => saveVariant(card.id)} style={{ fontSize: "0.75rem", padding: "2px 8px", background: "#28a745", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer" }}>Save</button>
                                    <button onClick={closeVariant} style={{ fontSize: "0.75rem", padding: "2px 8px", background: "#6c757d", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer" }}>Cancel</button>
                                  </div>
                                </div>
                              )}
                            </>
                        }
                      </td>

                      {/* Card Number */}
                      <td className="card-number-col" style={{ textAlign: "center" }}>
                        {isEditing
                          ? <input style={inp} value={editForm.card_number || ""} onChange={e => handleEditChange("card_number", e.target.value)} />
                          : card.front_image || card.back_image  // NOSONAR
                            ? <span role="link" tabIndex={0} style={{ cursor: "pointer", color: "#007bff", textDecoration: "underline" }} onClick={() => navigate(`/card-detail/${card.id}`, { state: { cardIds: sortedCards.map(c => c.id) } })} onKeyDown={(e) => { if (e.key === "Enter") navigate(`/card-detail/${card.id}`, { state: { cardIds: sortedCards.map(c => c.id) } }); }}>{card.card_number}</span>
                            : <span>{card.card_number}</span>}
                      </td>

                      {/* Rookie */}
                      <td className="rookie-col" style={{ textAlign: "center" }}>
                        {isEditing
                          ? <input type="checkbox" checked={!!Number(editForm.rookie)} onChange={e => handleEditChange("rookie", e.target.checked ? 1 : 0)} />
                          : rookieVal && <span className="rookie-badge">⭐</span>}
                      </td>

                      {/* Grade */}
                      <td className="grade-col" style={{ textAlign: "center" }}>
                        {isEditing ? (
                          <>
                            {enableGradedCards && (
                              <div style={{ display: "flex", justifyContent: "center", gap: "3px", marginBottom: "4px" }}>
                                {["Raw", "Graded"].map(mode => (
                                  <button key={mode}
                                    onClick={() => {
                                      if (mode === "Raw" && editForm.is_graded && editForm.value) {
                                        if (!window.confirm(`Convert to raw card? The graded value (${fmtDollar(Math.round(editForm.value))}) will be replaced by the book value formula.`)) return;
                                      }
                                      handleEditChange("is_graded", mode === "Graded");
                                    }}
                                    style={{ padding: "1px 6px", fontSize: "0.7rem", borderRadius: 3,
                                      border: "1px solid #ccc", cursor: "pointer",
                                      background: (editForm.is_graded ? "Graded" : "Raw") === mode ? "#1976d2" : "transparent",
                                      color: (editForm.is_graded ? "Graded" : "Raw") === mode ? "#fff" : "inherit" }}>
                                    {mode}
                                  </button>
                                ))}
                              </div>
                            )}
                            {editForm.is_graded
                              ? <>
                                  <select style={{ ...inp, marginBottom: "2px" }}
                                    value={editForm.grading_company || ""}
                                    onChange={e => handleEditChange("grading_company", e.target.value)}>
                                    <option value="">Co.</option>
                                    {["PSA","SGC","BGS"].map(c => <option key={c} value={c}>{c}</option>)}
                                  </select>
                                  <select style={{ ...inp, marginBottom: "2px" }}
                                    value={editForm.psa_grade || ""}
                                    onChange={e => handleEditChange("psa_grade", parseInt(e.target.value, 10))}>
                                    <option value="">Gr.</option>
                                    {[10,9,8,7,6,5,4,3,2,1].map(n => <option key={n} value={n}>{n}</option>)}
                                  </select>
                                  <input style={inp} placeholder="Serial #" maxLength={20}
                                    value={editForm.serial_number || ""}
                                    onChange={e => handleEditChange("serial_number", e.target.value || null)} />
                                </>
                              : <select style={inp} value={editForm.grade || ""} onChange={e => handleEditChange("grade", parseFloat(e.target.value))}>
                                  {(settings?.card_grades || []).map(gr => <option key={gr} value={gr}>{gr}</option>)}
                                </select>
                            }
                          </>
                        ) : (
                          card.is_graded
                            ? card.psa_grade && (
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                                  <span className="badge badge-grade" style={{ background: "#0ea5e9", color: "#fff", whiteSpace: "nowrap" }}>
                                    {card.grading_company ? `${card.grading_company} ${card.psa_grade}` : card.psa_grade}
                                  </span>
                                  {card.serial_number && (
                                    <span style={{ fontSize: "0.68rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{card.serial_number}</span>
                                  )}
                                </div>
                              )
                            : card.grade && <span className={`badge badge-grade ${gradeClass}`} title={{
                                3:   "NM-MT+",
                                1.5: "NM",
                                1:   "EX",
                                0.8: "VG",
                                0.4: "FR",
                                0.2: "PR",
                              }[g] || ""}>{card.grade}</span>
                        )}
                      </td>

                      {/* Book — freshness shown via left border on this cell */}
                      <td className="book-col" style={(() => {
                        if (card.is_graded) return { textAlign: "center" };
                        const hasValues = card.book_high || card.book_high_mid || card.book_mid || card.book_low_mid || card.book_low;
                        const freshnessColor = (() => {
                          if (!hasValues) return "#dc2626";
                          if (!card.book_values_updated_at) return "#dc2626";
                          const d = (Date.now() - new Date(card.book_values_updated_at)) / (1000 * 60 * 60 * 24);
                          if (d < 30) return null;
                          if (d < 90) return "#f59e0b";
                          return "#dc2626";
                        })();
                        return { textAlign: "center", ...(freshnessColor ? { borderLeft: `4px solid ${freshnessColor}` } : {}) };
                      })()} title={card.is_graded ? "Not applicable for graded cards" : card.book_values_updated_at
                        ? `Book values updated: ${new Date(card.book_values_updated_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}`
                        : "Book values never updated"
                      }>
                        {card.is_graded
                          ? <span style={{ fontSize: "0.75rem", color: "#9ca3af", fontStyle: "italic" }}>—</span>
                          : isEditing
                          ? <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                              {[["book_high","H","High (NM-MT+)"],["book_high_mid","HM","High Mid (NM)"],["book_mid","M","Mid (EX)"],["book_low_mid","LM","Low Mid (VG)"],["book_low","L","Low (PR)"]].map(([field, label, title]) => (
                                <div key={field} style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                                  <span style={{ fontSize: "0.7rem", color: "#888", width: "18px", flexShrink: 0 }}>{label}</span>
                                  <input style={inp} type="number" title={title} value={editForm[field] || ""} onChange={e => handleEditChange(field, e.target.value)} />
                                </div>
                              ))}
                            </div>
                          : <div>
                              {(!card.book_high && !card.book_high_mid && !card.book_mid && !card.book_low_mid && !card.book_low)  // NOSONAR
                                ? <span style={{ color: "#dc2626", fontWeight: 700, fontSize: "0.95rem" }} title="Book values never entered">!</span>
                                : <>
                                    {card.book_high && <span className="book-badge book-high" title="High (NM-MT+)">{card.book_high}</span>}
                                    {card.book_high_mid && <span className="book-badge book-highmid" title="High Mid (NM)">{card.book_high_mid}</span>}
                                    {card.book_mid && <span className="book-badge book-mid" title="Mid (EX)">{card.book_mid}</span>}
                                    {card.book_low_mid && <span className="book-badge book-lowmid" title="Low Mid (VG)">{card.book_low_mid}</span>}
                                    {card.book_low && <span className="book-badge book-low" title="Low (PR)">{card.book_low}</span>}
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleRefreshBook(card); }}
                                      title="Confirm book values are current (resets freshness timer)"
                                      style={{ background: "none", border: "none", cursor: "pointer",
                                               fontSize: "0.9rem", padding: "0 3px", color: "#0891b2",
                                               verticalAlign: "middle", lineHeight: 1 }}
                                    >↻</button>
                                  </>}
                              {bookEditMode && (
                                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                                  {BOOK_FIELDS.every(f => card[f]) && (
                                    <button
                                      title="Copy book values"
                                      onClick={(e) => { e.stopPropagation(); handleCopyBookValues(card); }}
                                      style={{ background: "none", border: "none", cursor: "pointer",
                                        fontSize: "0.65rem", letterSpacing: "0.05em", textTransform: "uppercase",
                                        color: "#0891b2", padding: "0 2px" }}
                                    >copy</button>
                                  )}
                                  {bookValueClipboard && (
                                    <button
                                      title="Paste book values"
                                      onClick={(e) => { e.stopPropagation(); handlePasteBookValues(card); }}
                                      style={{ background: "none", border: "none", cursor: "pointer",
                                        fontSize: "0.65rem", letterSpacing: "0.05em", textTransform: "uppercase",
                                        color: "#0891b2", padding: "0 2px", marginLeft: "auto" }}
                                    >paste</button>
                                  )}
                                </div>
                              )}
                            </div>}
                      </td>

                      {/* Market Factor — admin only */}
                      {isAdmin && (
                        <td className="market-col market-factor-col" style={{ textAlign: "center" }}>
                          {typeof card.market_factor === "number" ? (
                            (() => {
                              const f = card.market_factor;
                              let badgeClass = "badge-market-mid";
                              if (f >= 0.85) badgeClass = "badge-market-low";
                              else if (f <= 0.5) badgeClass = "badge-market-high";
                              return <span className={`badge-market ${badgeClass}`}>{f.toFixed(2)}</span>;
                            })()
                          ) : null}
                        </td>
                      )}

                      {/* Card Value — graded: manual entry + light-blue badge; raw: book-tier color */}
                      <td className="value-col" style={{ textAlign: "center" }}>
                        {isEditing
                          ? <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                              {editForm.is_graded && (
                                <input style={{ ...inp, width: 70 }} type="number" placeholder="Market $"
                                  value={editForm.value ?? ""}
                                  onChange={e => handleEditChange("value", e.target.value === "" ? null : parseFloat(e.target.value))} />
                              )}
                            </div>
                          : card.is_graded
                          ? <span className="badge badge-value" style={{ backgroundColor: "#e0f2fe", color: "#0369a1" }}>
                              {card.value != null ? fmtDollar(Math.round(card.value)) : <span style={{ color: "#9ca3af" }}>—</span>}
                            </span>
                          : (() => {
                              const v = Math.round(Number(card.value) || 0);
                              const bH  = Number(card.book_high)     || null;
                              const bHM = Number(card.book_high_mid)  || null;
                              const bM  = Number(card.book_mid)       || null;
                              const bLM = Number(card.book_low_mid)   || null;
                              if (v === 0)
                                return <span className="badge badge-value" style={{ backgroundColor: "#e5e7eb", color: "#9ca3af" }}>{fmtDollar(v)}</span>;
                              let valueClass = "book-low";
                              if (bH  && v > bH)   valueClass = "value-above-book";
                              else if (bH  && v >= bH)  valueClass = "book-high";
                              else if (bHM && v >= bHM) valueClass = "book-highmid";
                              else if (bM  && v >= bM)  valueClass = "book-mid";
                              else if (bLM && v >= bLM) valueClass = "book-lowmid";
                              return <span className={`badge badge-value ${valueClass}`}>{fmtDollar(v)}</span>;
                            })()
                        }
                        {/* PP / PD chips — always visible, click to edit inline */}
                        {(() => {
                          const chipStyle = { display: "inline-block", cursor: "pointer", marginTop: 3,
                            background: "var(--bg-muted)", border: "1px solid var(--border)",
                            borderRadius: 10, padding: "1px 6px",
                            fontSize: "0.68rem", color: "var(--text-muted)", whiteSpace: "nowrap" };
                          const inpStyle = { fontSize: "0.68rem", padding: "1px 4px", borderRadius: 4, width: 70,
                            border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text-primary)" };
                          return (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, marginTop: 2 }}>
                              {chipEdit?.id === card.id && chipEdit?.field === "price_paid" ? (
                                <input autoFocus type="number" step="0.01" min="0" style={inpStyle}
                                  value={chipEdit.value}
                                  onChange={e => setChipEdit(prev => ({ ...prev, value: e.target.value }))}
                                  onBlur={saveChip}
                                  onKeyDown={e => { if (e.key === "Enter") saveChip(); if (e.key === "Escape") setChipEdit(null); }} />
                              ) : (
                                <span role="button" tabIndex={0} style={chipStyle} title="Purchase Price"
                                  onClick={() => setChipEdit({ id: card.id, field: "price_paid", value: card.price_paid != null ? String(card.price_paid) : "" })}
                                  onKeyDown={e => e.key === "Enter" && setChipEdit({ id: card.id, field: "price_paid", value: card.price_paid != null ? String(card.price_paid) : "" })}>
                                  {card.price_paid != null ? `$${parseFloat(card.price_paid).toLocaleString()}` : "＋ PP"}
                                </span>
                              )}
                              {chipEdit?.id === card.id && chipEdit?.field === "purchase_date" ? (
                                <input autoFocus type="date" style={{ ...inpStyle, width: "auto" }}
                                  value={chipEdit.value}
                                  onChange={e => setChipEdit(prev => ({ ...prev, value: e.target.value }))}
                                  onBlur={saveChip}
                                  onKeyDown={e => { if (e.key === "Enter") saveChip(); if (e.key === "Escape") setChipEdit(null); }} />
                              ) : (
                                <span role="button" tabIndex={0} style={chipStyle} title="Purchase Date"
                                  onClick={() => setChipEdit({ id: card.id, field: "purchase_date", value: card.purchase_date ?? "" })}
                                  onKeyDown={e => e.key === "Enter" && setChipEdit({ id: card.id, field: "purchase_date", value: card.purchase_date ?? "" })}>
                                  {card.purchase_date ? new Date(card.purchase_date + "T00:00:00").toLocaleDateString() : "＋ PD"}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>

                      {/* Card Image */}
                      <td className="image-col" style={{ textAlign: "center" }}>
                        {isEditing ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: "3px", alignItems: "center" }}>
                            {/* Front upload */}
                            {editForm.front_image && (
                              <img src={editForm.front_image} alt="Front" style={{ width: 40, height: "auto", marginBottom: 2 }} />
                            )}
                            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                              <label style={{ fontSize: "0.7rem", color: "#1976d2", cursor: "pointer", textDecoration: "underline" }}
                                title="Upload front image">
                                {editForm.front_image ? "↑ Front" : "+ Front"}
                                <input type="file" accept="image/*" style={{ display: "none" }}
                                  onChange={e => { if (e.target.files[0]) { setInlineEditorFile(e.target.files[0]); setInlineEditorSide("front"); setInlineEditorCardId(card.id); e.target.value = ""; } }} />
                              </label>
                              {editForm.front_image && (
                                <span style={{ fontSize: "0.7rem", color: "#c62828", cursor: "pointer", lineHeight: 1 }}
                                  title="Delete front image"
                                  onClick={() => handleDeleteImage(card.id, "front")}>✕</span>
                              )}
                            </div>
                            {/* Back upload */}
                            {editForm.back_image && (
                              <img src={editForm.back_image} alt="Back" style={{ width: 40, height: "auto", marginBottom: 2 }} />
                            )}
                            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                              <label style={{ fontSize: "0.7rem", color: "#6c757d", cursor: "pointer", textDecoration: "underline" }}
                                title="Upload back image">
                                {editForm.back_image ? "↑ Back" : "+ Back"}
                                <input type="file" accept="image/*" style={{ display: "none" }}
                                  onChange={e => { if (e.target.files[0]) { setInlineEditorFile(e.target.files[0]); setInlineEditorSide("back"); setInlineEditorCardId(card.id); e.target.value = ""; } }} />
                              </label>
                              {editForm.back_image && (
                                <span style={{ fontSize: "0.7rem", color: "#c62828", cursor: "pointer", lineHeight: 1 }}
                                  title="Delete back image"
                                  onClick={() => handleDeleteImage(card.id, "back")}>✕</span>
                              )}
                            </div>
                          </div>
                        ) : card.front_image ? (  // NOSONAR
                          <img
                            src={card.front_image}
                            alt="Front"
                            style={{ width: "50px", height: "auto", cursor: "pointer" }}
                            tabIndex={0}
                            onClick={() => setSelectedCard(card)}
                            onKeyDown={(e) => { if (e.key === "Enter") setSelectedCard(card); }}
                            onMouseEnter={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setHoverPos({ x: rect.right + 8, y: rect.top });
                              setHoverCard(card);
                            }}
                            onMouseMove={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setHoverPos({ x: rect.right + 8, y: rect.top });
                            }}
                            onMouseLeave={() => setHoverCard(null)}
                          />
                        ) : (
                          <span style={{ filter: "grayscale(1)", opacity: 0.35, fontSize: "1.1rem" }} title="No image on file">📷</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="action-col actions-col" style={{ textAlign: "center" }}>
                        {isEditing ? (
                          <div style={{ display: "flex", gap: "0.4rem", justifyContent: "center" }}>
                            <button
                              onClick={() => handleEditSave(card.id)}
                              style={{ background: "#28a745", color: "white", border: "none", borderRadius: "4px", padding: "4px 10px", cursor: "pointer", fontSize: "0.85rem", fontWeight: "bold", width: "auto" }}
                            >✓ Save</button>
                            <button
                              onClick={handleEditCancel}
                              style={{ background: "#6c757d", color: "white", border: "none", borderRadius: "4px", padding: "4px 10px", cursor: "pointer", fontSize: "0.85rem", fontWeight: "bold", width: "auto" }}
                            >✗ Cancel</button>
                          </div>
                        ) : (
                          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
                            <button
                              onClick={() => handlePinRow(card.id)}
                              style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.2rem", padding: "2px 4px", width: "auto",
                                opacity: Number(card.id) === Number(pinnedRowId) ? 1 : 0.5,
                                color: Number(card.id) === Number(pinnedRowId) ? "#ff0000" : "inherit",
                                transform: Number(card.id) === Number(pinnedRowId) ? "none" : "rotate(45deg)",
                                transition: "opacity 0.15s, color 0.15s" }}
                              title={Number(card.id) === Number(pinnedRowId) ? "Unpin row" : "Pin row"}
                            >📌</button>
                            <button
                              onClick={() => handleEditStart(card)}
                              style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.5rem", padding: "2px 4px", color: "#1976d2", width: "auto" }}
                              title="Edit"
                            >✏️</button>
                            <button
                              onClick={() => handleDuplicate(card)}
                              style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.5rem", padding: "2px 4px", color: "#28a745", width: "auto" }}
                              title="Copy"
                            >📋</button>
                            <button
                              onClick={() => (tierAllowsLabels && !card.is_graded) ? handlePrintLabel(card) : undefined}
                              disabled={labelLoading === card.id || !tierAllowsLabels || card.is_graded}
                              style={{ background: "none", border: "none", fontSize: "1.5rem", padding: "2px 4px", width: "auto",
                                cursor: (tierAllowsLabels && !card.is_graded) ? "pointer" : "default",
                                opacity: (tierAllowsLabels && !card.is_graded) ? 1 : 0.25 }}
                              title={tierAllowsLabels ? "Print label" : "Label printing requires Elite plan"}
                            >🖨️</button>
                            <button
                              onClick={() => navigate(`/card-detail/${card.id}`, { state: { cardIds: sortedCards.map(c => c.id) } })}
                              style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.5rem", padding: "2px 4px", color: "#6c757d", width: "auto" }}
                              title="Card detail"
                            >ℹ️</button>
                            <button
                              onClick={() => handleDelete(card)}
                              style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.5rem", padding: "2px 4px", color: "#dc3545", width: "auto" }}
                              title="Delete"
                            >✕</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {displayedCards.length === 0 && editingCardId !== "new" && (
                  <tr>
                    <td colSpan={14} style={{ textAlign: "center", padding: "2.5rem 1rem", color: "var(--text-muted)" }}>
                      No cards yet. Click <strong>＋</strong> above to add your first card, or{" "}
                      <span
                        role="button" tabIndex={0}
                        onClick={() => navigate("/import-cards")}
                        onKeyDown={e => e.key === "Enter" && navigate("/import-cards")}
                        style={{ color: "var(--accent-blue)", cursor: "pointer", textDecoration: "underline" }}
                      >import from CSV</span>.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {hasMore && !loading && (
              <div style={{ textAlign: "center", padding: "1rem 0" }}>
                <button className="nav-btn" onClick={() => fetchCards(currentSkip + FETCH_LIMIT, true)}>
                  Load More ({total - cards.length} remaining)
                </button>
              </div>
            )}
        </div>
      )}
      {deleteConfirmCard && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 3000,
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <div style={{
            background: "var(--bg-card, #fff)", color: "var(--text, #1a1a1a)",
            border: "1px solid var(--border, #ddd)",
            borderRadius: 10, padding: "1.5rem 2rem",
            boxShadow: "0 4px 24px rgba(0,0,0,0.35)", minWidth: 280, textAlign: "center"
          }}>
            <p style={{ margin: "0 0 1.25rem", fontSize: "1rem", fontWeight: 600, color: "var(--text, #1a1a1a)" }}>
              Delete {deleteConfirmCard.first_name} {deleteConfirmCard.last_name}?
            </p>
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
              <button className="nav-btn secondary" onClick={() => setDeleteConfirmCard(null)}>Cancel</button>
              <button className="nav-btn" style={{ background: "#dc3545", borderColor: "#dc3545" }} onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
      {selectedCard && (
        <CardImages card={selectedCard} onClose={() => { setSelectedCard(null); tableSectionRef.current?.focus(); }} onEdit={handleEditExistingImage} />
      )}
      {hoverCard?.front_image && (
        <div
          style={{
            position: "fixed",
            left: hoverPos.x,
            top: hoverPos.y,
            zIndex: 2000,
            pointerEvents: "none",
            borderRadius: 8,
            boxShadow: "0 4px 20px rgba(0,0,0,0.35)",
            background: "var(--bg-card, #fff)",
            padding: "4px",
            border: "1px solid var(--border, #e0e0e0)",
          }}
        >
          <img
            src={hoverCard.front_image}
            alt="Preview"
            style={{ width: 220, height: "auto", display: "block", borderRadius: 5 }}
          />
        </div>
      )}
      <LabelPreviewModal
        labelData={labelData}
        onPrint={() => window.open(`/card-label/${labelData?.id}`, "_blank")}
        onClose={() => { setLabelData(null); tableSectionRef.current?.focus(); }}
      />
    </div>
    {toast && (
      <div style={{
        position: "fixed", bottom: "1.5rem", left: "1.5rem",
        background: "#1a7a1a", color: "white", padding: "0.6rem 1.2rem",
        borderRadius: "8px", fontSize: "0.9rem", zIndex: 9999,
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
      }}>{toast}</div>
    )}
    {showSortModal && (
      <div data-tour="sort-modal">
        <SortModal
          sortConfig={sortConfig}
          defaultSort={settings?.default_sort || []}
          onApply={(levels) => { clearPin(); clearCloneAnchor(); setSortConfig(levels); }}
          onClose={() => { setShowSortModal(false); tableSectionRef.current?.focus(); }}
        />
      </div>
    )}

    {/* AI card scan modal (inline add row camera button) */}
    {showScanModal && (
      <div
        className="modal-overlay"
        role="button"  // NOSONAR
        tabIndex={0}
        onClick={() => setShowScanModal(false)}
        onKeyDown={e => { if (e.key === "Escape") setShowScanModal(false); }}
      >
        <div
          className="modal-box"
          style={{ width: 420, maxWidth: "95vw" }}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        >
          <h3 style={{ marginTop: 0 }}>📷 Identify Card from Photo</h3>
          <label style={{ display: "block", cursor: "pointer", marginBottom: "0.75rem" }}>
            {scanPreview ? (
              <img src={scanPreview} alt="Preview" style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 8, objectFit: "contain", border: "1px solid #ddd" }} />
            ) : (
              <div style={{ border: "2px dashed #ccc", borderRadius: 10, padding: "1.5rem", textAlign: "center", color: "#888" }}>
                📷 Tap to choose a photo or capture
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={e => {
                const f = e.target.files?.[0];
                if (!f) return;
                setScanFile(f);
                setScanPreview(URL.createObjectURL(f));
                setScanError(null);
              }}
            />
          </label>
          {scanError && (
            <p style={{ color: "#dc3545", fontSize: "0.85rem", margin: "0 0 0.5rem" }}>{scanError}</p>
          )}
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
            <button
              className="nav-btn"
              disabled={!scanFile || scanning}
              onClick={handleScanIdentify}
            >
              {scanning ? "Identifying…" : "🔍 Identify"}
            </button>
            <button
              className="nav-btn secondary"
              onClick={() => { setShowScanModal(false); setScanFile(null); setScanPreview(null); setScanError(null); }}
            >
              Cancel
            </button>
          </div>
          <p style={{ fontSize: "0.78rem", color: "#888", marginTop: "0.75rem", marginBottom: 0, textAlign: "center" }}>
            Fields will be pre-filled in the add form after identification.
          </p>
        </div>
      </div>
    )}
    {inlineEditorFile && (
      <ImageEditor
        file={inlineEditorFile}
        title={`Edit ${inlineEditorSide === "front" ? "Front" : "Back"} Photo`}
        onSave={(editedFile) => {
          handleImageUpload(inlineEditorCardId, inlineEditorSide, editedFile);
          setInlineEditorFile(null); setInlineEditorSide(null);
          setInlineEditorCardId(null); setInlineEditorIsExisting(false);
        }}
        onCancel={() => {
          setInlineEditorFile(null); setInlineEditorSide(null);
          setInlineEditorCardId(null); setInlineEditorIsExisting(false);
        }}
        onDelete={inlineEditorIsExisting ? () => {
          handleDeleteImage(inlineEditorCardId, inlineEditorSide);
          setInlineEditorFile(null); setInlineEditorSide(null);
          setInlineEditorCardId(null); setInlineEditorIsExisting(false);
        } : undefined}
      />
    )}
    </>
  );
}
